import Navigation from "@/components/Navigation";
import { Card } from "@/components/ui/card";
import { Helmet } from "react-helmet";
import SchemaMarkup from "@/components/SchemaMarkup";
import Breadcrumb from "@/components/Breadcrumb";
import { createArticleSchema } from "@/utils/schemaHelpers";
import HoroscopeContent from "@/components/HoroscopeContent";

const HoroscopoSagitario = () => {
  const articleSchema = createArticleSchema(
    "Sagitario: Horóscopo Diario, Características y Compatibilidad con los 12 Signos",
    "Descubre el horóscopo diario, semanal y mensual de Sagitario. Predicciones de amor, trabajo, salud y dinero para personas nacidas entre el 22 de noviembre y el 21 de diciembre.",
    `${window.location.origin}/horoscopo-sagitario`
  );

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Inicio",
        "item": window.location.origin
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": "Horóscopo Sagitario",
        "item": `${window.location.origin}/horoscopo-sagitario`
      }
    ]
  };

  const breadcrumbItems = [
    { name: "Inicio", url: "/" },
    { name: "Horóscopo", url: "/horoscopo" },
    { name: "Sagitario" }
  ];

  return (
    <>
      <Helmet>
        <title>Sagitario: Horóscopo Diario, Características y Compatibilidad con los 12 Signos</title>
        <meta name="description" content="Descubre el horóscopo diario, semanal y mensual de Sagitario. Predicciones de amor, trabajo, salud y dinero para personas nacidas entre el 22 de noviembre y el 21 de diciembre." />
        <link rel="canonical" href={`${window.location.origin}/horoscopo-sagitario`} />
        <meta property="og:title" content="Sagitario: Horóscopo Diario, Características y Compatibilidad" />
        <meta property="og:description" content="Descubre el horóscopo diario, semanal y mensual de Sagitario. Predicciones de amor, trabajo, salud y dinero." />
        <meta property="og:url" content={`${window.location.origin}/horoscopo-sagitario`} />
        <meta property="og:type" content="article" />
      </Helmet>
      <SchemaMarkup schema={articleSchema} />
      <SchemaMarkup schema={breadcrumbSchema} />
      
      <div className="min-h-screen">
        <Navigation />
        
        <main className="pt-24 pb-12 px-4">
          <div className="container mx-auto max-w-4xl">
            <Breadcrumb items={breadcrumbItems} />
            <div className="text-center mb-12">
              <div className="text-6xl mb-4">♐</div>
              <h1 className="text-2xl font-bold mb-4 text-glow">
                Horóscopo de Sagitario
              </h1>
              <p className="text-xl text-muted-foreground">22 de Noviembre - 21 de Diciembre</p>
            </div>

            <section className="mb-16">
              <h2 className="text-2xl font-bold mb-6 text-primary">Tu Horóscopo Sagitario: Predicciones Actualizadas</h2>
              <HoroscopeContent sign="sagitario" signName="Sagitario" />
            </section>

            <section className="mb-16">
              <h2 className="text-2xl font-bold mb-6 text-secondary">Características Profundas del Signo Sagitario: El Filósofo Viajero</h2>
              
              <Card className="p-8 mb-6 gradient-mystic">
                <h3 className="text-xl font-bold mb-4">Los Pilares Astrológicos de Sagitario</h3>
                <div className="grid md:grid-cols-3 gap-6">
                  <div>
                    <h4 className="font-bold text-base mb-2 text-primary">🔥 Elemento: Fuego</h4>
                    <p className="text-muted-foreground">Enfoque en la expansión, el conocimiento y la aventura. Sagitario arde con pasión por la verdad.</p>
                  </div>
                  <div>
                    <h4 className="font-bold text-base mb-2 text-secondary">🏹 Modalidad: Mutable</h4>
                    <p className="text-muted-foreground">Define la búsqueda constante, el optimismo y la enseñanza. Adaptable en métodos, firme en visión.</p>
                  </div>
                  <div>
                    <h4 className="font-bold text-base mb-2 text-accent">♃ Planeta Regente: Júpiter</h4>
                    <p className="text-muted-foreground">Dios de la Suerte, la Expansión y la Sabiduría. Bendición de abundancia y crecimiento.</p>
                  </div>
                </div>
              </Card>

              <Card className="p-8 mb-6 gradient-celestial">
                <h3 className="text-xl font-bold mb-4">Perfil de Personalidad Sagitario: Fortalezas y Debilidades</h3>
                <div className="grid md:grid-cols-2 gap-6 mb-6">
                  <div>
                    <h4 className="font-bold text-base mb-3 text-primary">✨ Fortalezas</h4>
                    <ul className="space-y-2 text-muted-foreground">
                      <li>• <strong>Optimismo:</strong> Fe inquebrantable en el futuro</li>
                      <li>• <strong>Honestidad:</strong> Verdad directa y sin filtros</li>
                      <li>• <strong>Aventura:</strong> Espíritu explorador incansable</li>
                      <li>• <strong>Sabiduría:</strong> Búsqueda filosófica profunda</li>
                      <li>• <strong>Espíritu Libre:</strong> Independencia genuina</li>
                    </ul>
                  </div>
                  <div>
                    <h4 className="font-bold text-base mb-3 text-secondary">⚠️ Debilidades</h4>
                    <ul className="space-y-2 text-muted-foreground">
                      <li>• <strong>Exceso de confianza:</strong> Subestima riesgos</li>
                      <li>• <strong>Falta de tacto:</strong> Honestidad brutal hiriente</li>
                      <li>• <strong>Impaciencia:</strong> Dificultad con detalles</li>
                      <li>• <strong>Prometer demasiado:</strong> Entusiasmo poco realista</li>
                      <li>• <strong>Inquietud:</strong> Dificultad para comprometerse</li>
                    </ul>
                  </div>
                </div>
                <div className="text-center p-4 bg-primary/10 rounded-lg">
                  <p className="text-lg font-bold text-primary">Mantra Clave: "Yo Veo"</p>
                </div>
              </Card>

              <Card className="p-8 mb-6 gradient-mystic">
                <h3 className="text-xl font-bold mb-4">Sagitario en el Amor: La Libertad ante Todo y la Pareja como Compañero</h3>
                <p className="text-muted-foreground leading-relaxed mb-4">
                  Sagitario busca una pareja que sea compañera de aventuras, no una ancla. Valora la libertad por encima de todo y necesita espacio para explorar el mundo física y filosóficamente. El romance debe ser una aventura constante, llena de descubrimientos y crecimiento mutuo.
                </p>
                <p className="text-muted-foreground leading-relaxed">
                  Necesita una relación donde pueda mantener su independencia mientras comparte experiencias significativas. Su honestidad brutal puede herir, pero siempre viene de un lugar de autenticidad. Cuando encuentra a alguien que comparte su amor por la aventura y la búsqueda de verdad, es increíblemente leal y generoso.
                </p>
              </Card>

              <Card className="p-8 gradient-celestial">
                <h3 className="text-xl font-bold mb-4">Sagitario en el Trabajo y Finanzas: Educación Superior, Viajes y Leyes</h3>
                <p className="text-muted-foreground leading-relaxed mb-4">
                  Sagitario destaca en educación superior, filosofía, derecho, turismo, comercio internacional, publicación y cualquier campo que involucre expansión de horizontes. Su visión de futuro y optimismo lo hacen excelente en emprendimientos y estrategia a largo plazo.
                </p>
                <p className="text-muted-foreground leading-relaxed">
                  Con las finanzas, Sagitario es optimista, a veces demasiado. Puede tomar riesgos calculados pero también ser descuidado con detalles. Generoso con su dinero y propenso a invertir en experiencias y educación sobre posesiones materiales. Excelente para negocios internacionales.
                </p>
              </Card>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-6 text-accent">Compatibilidad de Sagitario con los Otros Signos Zodiacales</h2>
              
              <Card className="p-8 mb-6 gradient-mystic">
                <h3 className="text-xl font-bold mb-4">💚 Tu Match Perfecto</h3>
                <div className="space-y-4">
                  <div>
                    <h4 className="font-bold text-base mb-2">♈ Aries y ♌ Leo</h4>
                    <p className="text-muted-foreground">
                      Los signos de fuego comparten pasión por la vida y amor por la aventura. Con Aries hay química explosiva y espíritu competitivo saludable. Con Leo, una conexión de optimismo compartido y grandeza mutua.
                    </p>
                  </div>
                  <div>
                    <h4 className="font-bold text-base mb-2">♒ Acuario</h4>
                    <p className="text-muted-foreground">
                      Acuario ofrece la libertad intelectual que Sagitario anhela. Ambos valoran la independencia, el idealismo y la visión de futuro. Una conexión basada en amistad, respeto mutuo y objetivos compartidos.
                    </p>
                  </div>
                </div>
              </Card>

              <Card className="p-8 gradient-celestial">
                <h3 className="text-xl font-bold mb-4">⚡ Los Desafíos y Lecciones de Sagitario</h3>
                <p className="text-muted-foreground mb-4">
                  La atención al detalle práctico es el gran desafío de Sagitario. Los signos de tierra (Tauro, Virgo, Capricornio) le enseñan sobre la importancia de los detalles, la consistencia y transformar visiones en realidad tangible.
                </p>
                <p className="text-muted-foreground">
                  Géminis, su signo opuesto, lo desafía con multiplicidad de perspectivas versus una sola verdad. Los signos de agua (Cáncer, Escorpio, Piscis) le enseñan sobre profundidad emocional y la importancia de las raíces junto con las alas.
                </p>
              </Card>
            </section>
          </div>
        </main>
      </div>
    </>
  );
};

export default HoroscopoSagitario;
